import { pb } from "./pocketbase";
import { isetSyncService } from "./isetSyncService";
import { financeiroIntegracaoService } from "./financeiroIntegracaoService";

export const OFFICE_ADDRESS = {
  cep: "03533-001",
  logradouro: "Av. Waldemar Carlos Pereira",
  numero: "179",
  complemento: "Sala 6",
  bairro: "Vila Talarico",
  cidade: "São Paulo",
  estado: "SP",
  referencia: "Sempre Joias Escritório"
};

// Helper para aplicar patch profundo em objetos (ex: "order.customer.name" -> obj.order.customer.name)
const applyPatch = (obj, path, value) => {
  const keys = path.split(".");
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!current[keys[i]]) current[keys[i]] = {}; // Cria se não existir
    current = current[keys[i]];
  }
  current[keys[keys.length - 1]] = value;
};

// Mapa de campos que precisam ser sincronizados com colunas Flat para busca
const FLAT_FIELD_MAP = {
  "order.customer.name": "customer_name",
  "order.number": "order_number",
  "shipping.address.city": "shipping_city",
  "shipping.address.statecode": "shipping_state",
  "shipping.address.state": "shipping_state", // Fallback
};

const DB_LOGISTICS_STATUSES = [
  "SEM ETIQUETA",
  "PRONTO PARA POSTAR",
  "ENVIADO",
  "AGUARDANDO RETIRADA",
  "MOTOBOY",
  "ML",
  "FOTO",
  "AGUARDANDO",
  "PRONTO EM ESPERA",
  "FELIPPE",
  "REPOSTAGEM",
  "CANCELADO",
  "GOLPE",
  "REVISAO",
  "AGUARDANDO POSTAGEM",
];

const getDbLogisticsStatus = (status) => {
  if (status === "RETIRADA") return "AGUARDANDO RETIRADA";
  if (DB_LOGISTICS_STATUSES.includes(status)) return status;
  return null; // Unsupported by DB column, saved in full_data only
};

export const ordersService = {
  // --- 1. MUDAR STATUS LOGÍSTICO (EM LOTE) ---
  async updateLogisticsStatusBatch(ids, newStatus) {
    const dbStatus = getDbLogisticsStatus(newStatus);
    
    // 1. Atualiza todos os production_orders e retorna os registros
    const records = await Promise.all(
      ids.map(async (id) => {
        const record = await pb.collection("production_orders").getOne(id);
        const fullData = record.full_data || {};
        fullData.logisticsStatus = newStatus;
        
        const payload = {
          full_data: fullData
        };
        if (dbStatus !== null) {
          payload.logistics_status = dbStatus;
        }
        
        const updatedRecord = await pb.collection("production_orders").update(id, payload);

        // Dispara a sincronização com a iSET via n8n se for pedido de origem ISET
        if (newStatus === "AGUARDANDO POSTAGEM" && fullData.source === "n8n") {
          try {
            const orderNum = updatedRecord.order_number || fullData.order?.number || "AVULSO";
            isetSyncService.enqueueEvent("em_tratamento", orderNum, updatedRecord.id);
          } catch (err) {
            console.error("Erro ao enfileirar sincronização ISET:", err);
          }
        }

        if (newStatus === "ENVIADO" && fullData.source === "n8n") {
          try {
            const orderNum = updatedRecord.order_number || fullData.order?.number || "AVULSO";
            
            // Busca Nota Fiscal autorizada para anexar
            const invoices = await pb.collection("fiscal_invoices").getFullList({
              filter: `order_number="${orderNum}" && nfe_status="autorizada"`,
              sort: "-created",
              limit: 1
            });
            const activeInvoice = invoices.length > 0 ? invoices[0] : null;
            const trackingCode = fullData.correios_codigo_objeto || fullData.shipping?.tracking || "";

            const additionalData = {
              trackingCode
            };

            if (activeInvoice) {
              let nfeDate = "";
              try {
                const rawDate = activeInvoice.nfe_data_emissao || activeInvoice.full_data?.nfe_data_emissao || activeInvoice.created;
                nfeDate = new Date(rawDate).toISOString().split("T")[0];
              } catch (e) {
                nfeDate = new Date().toISOString().split("T")[0];
              }

              additionalData.nfe = {
                number: String(activeInvoice.nfe_numero || ""),
                key: String(activeInvoice.nfe_chave || ""),
                serie: String(activeInvoice.nfe_serie || "1"),
                date: nfeDate,
                link: activeInvoice.nfe_pdf || activeInvoice.nfe_pdf_url || ""
              };
            }

            isetSyncService.enqueueEvent("pronto_postar", orderNum, updatedRecord.id, additionalData);
          } catch (err) {
            console.error("Erro ao enfileirar sincronização ISET de postagem:", err);
          }
        }

        return updatedRecord;
      })
    );

    // 2. Processa Gatilho MOTOBOY de forma agrupada e sequencial para evitar duplicatas por concorrência
    if (newStatus === "MOTOBOY") {
      try {
        const deliveriesToProcess = [];

        for (const record of records) {
          const fullData = record.full_data || {};
          const orderNumber = record.order_number || fullData.order?.number || "AVULSO";
          const isAvulso = !orderNumber || orderNumber === "AVULSO";
          
          // Agrupa por número de pedido para que múltiplos itens do mesmo pedido não criem múltiplos registros de entrega
          const existsInQueue = deliveriesToProcess.find(d => 
            !isAvulso && d.numero_pedido === orderNumber
          );

          if (!existsInQueue) {
            deliveriesToProcess.push({
              id: record.id,
              record: record,
              fullData: fullData,
              numero_pedido: orderNumber,
              isAvulso: isAvulso
            });
          }
        }

        // Execução sequencial para evitar race condition
        for (const item of deliveriesToProcess) {
          let existing = [];
          
          if (!item.isAvulso) {
            existing = await pb.collection("motoboy_entregas").getFullList({
              filter: `numero_pedido='${item.numero_pedido.replace(/'/g, "\\'")}' && status != 'entregue' && status != 'cancelado'`
            });
          } else {
            existing = await pb.collection("motoboy_entregas").getFullList({
              filter: `pedido_id='${item.id}'`
            });
          }

          if (existing.length > 0) {
            const deliv = existing[0];
            if (deliv.status !== "pendente") {
              await pb.collection("motoboy_entregas").update(deliv.id, {
                status: "pendente",
                data_solicitacao: new Date().toISOString(),
                solicitante_id: pb.authStore.model?.id || deliv.solicitante_id || "",
                endereco_retirada: deliv.endereco_retirada || OFFICE_ADDRESS
              });
            }
          } else {
            const address = item.fullData.shipping?.address || {};
            await pb.collection("motoboy_entregas").create({
              pedido_id: item.id,
              numero_pedido: item.numero_pedido,
              cliente_nome: item.record.customer_name || item.fullData.order?.customer?.name || "Cliente Balcão",
              endereco: {
                logradouro: address.street || "",
                numero: address.number || "",
                complemento: address.complemento || "",
                bairro: address.bairro || "",
                cidade: address.city || "",
                estado: address.statecode || address.state || "",
                cep: address.zip || "",
                referencia: ""
              },
              motoboy_id: "",
              status: "pendente",
              data_solicitacao: new Date().toISOString(),
              solicitante_id: pb.authStore.model?.id || "",
              endereco_retirada: OFFICE_ADDRESS
            });
          }
        }
      } catch (err) {
        console.error("Erro no gatilho automático MOTOBOY:", err);
      }
    }

    return records;
  },

  // --- 2. MUDAR STATUS DE ITEM INDIVIDUAL ---
  async updateItemStatus(itemId, newStatus) {
    await pb.collection("production_orders").update(itemId, {
      status: newStatus,
      // lastUpdate gerado automaticamente pelo PB ('updated')
    });

    if (newStatus === "CANCELADO" || newStatus === "GOLPE") {
      try {
        await financeiroIntegracaoService.triggerCancellation("production_orders", itemId, pb.authStore.model);
      } catch (errFin) {
        console.warn("Erro ao cancelar pendência financeira do Pedido de Fábrica:", errFin);
      }
    }
  },

  // --- 3. DELETAR PEDIDO (EM LOTE) ---
  async deleteOrderBatch(ids) {
    // Dispara o cancelamento financeiro antes de apagar do banco
    for (const id of ids) {
      try {
        await financeiroIntegracaoService.triggerCancellation("production_orders", id, pb.authStore.model);
      } catch (errFin) {
        console.warn("Erro ao disparar estorno/cancelamento para pedido excluído:", errFin);
      }
    }

    const promises = ids.map((id) =>
      pb.collection("production_orders").delete(id)
    );
    await Promise.all(promises);
  },

  // --- 4. ARQUIVAR PEDIDO (EM LOTE) ---
  async archiveOrderBatch(ids) {
    const promises = ids.map(async (id) => {
      const record = await pb.collection("production_orders").getOne(id);
      const fullData = record.full_data || {};
      
      const prevStatus = record.logistics_status || fullData.logisticsStatus || "";
      if (prevStatus === "ENVIADO" && fullData.source === "n8n") {
        try {
          const orderNum = record.order_number || fullData.order?.number || "AVULSO";
          isetSyncService.enqueueEvent("arquivado_enviado", orderNum, record.id);
        } catch (err) {
          console.error("Erro ao enfileirar sincronização ISET no arquivamento:", err);
        }
      }

      return pb.collection("production_orders").update(id, {
        archived: true,
      });
    });
    await Promise.all(promises);
  },

  // --- 4.1. DESARQUIVAR ---
  async unarchiveOrderBatch(ids) {
    const promises = ids.map((id) =>
      pb.collection("production_orders").update(id, {
        archived: false,
      })
    );
    await Promise.all(promises);
  },

  // --- 5. ATUALIZAR DADOS DO PEDIDO (EDITZÃO GIGANTE - MODAL) ---
  async updateOrderDetailsBatch(items, newData) {
    // Definimos o mapa de atualizações (Dot Notation -> Valor)
    const updateMap = {
      // Dados Básicos
      "order.number": newData.orderNumber,
      "order.notes": newData.notes,
      exception: newData.exception, // Top Level (Siren)

      // Cliente
      "order.customer.name": newData.customerName,
      "order.customer.cpf": newData.customerCpf,
      "order.customer.phone": newData.customerPhone,
      "order.customer.email": newData.customerEmail,

      // Logística
      "shipping.tipoenvio": newData.shippingMethod,
      "shipping.price": newData.shippingPrice,
      "shipping.tracking": newData.tracking,

      // Endereço
      "shipping.address.zip": newData.zip,
      "shipping.address.street": newData.street,
      "shipping.address.number": newData.number,
      "shipping.address.complemento": newData.comp,
      "shipping.address.bairro": newData.district,
      "shipping.address.city": newData.city,
      "shipping.address.statecode": newData.state,

      // Financeiro
      "order.payment.method": newData.paymentMethod,
    };

    if (newData.totalValueOverride) {
      updateMap["order.payment.total"] = newData.totalValueOverride;
    }

    // Processa item por item (ineficiente, mas necessario para Deep Patch)
    const promises = items.map(async (item) => {
      // 1. Pega o registro atual para ter o 'full_data' completo
      const record = await pb.collection("production_orders").getOne(item.id);

      const currentFullData = record.full_data || {};
      const payload = { ...currentFullData }; // Clone
      const flatUpdates = {};

      // 2. Aplica os Patches
      Object.entries(updateMap).forEach(([key, value]) => {
        if (value === undefined) return;

        // Atualiza dentro do JSONzao
        if (key === "exception") {
          flatUpdates.exception = value; // Exception é top-level
        } else {
          applyPatch(payload, key, value);
        }

        // Atualiza Coluna Flat se existir mapeamento
        if (FLAT_FIELD_MAP[key]) {
          flatUpdates[FLAT_FIELD_MAP[key]] = value;
        }
      });

      // 3. Salva
      await pb.collection("production_orders").update(item.id, {
        full_data: payload,
        ...flatUpdates, // Spread das colunas flat (order_number, customer_name, etc)
      });
    });

    await Promise.all(promises);
  },

  // --- 6. SALVAR EDIÇÃO DE ITEM (SPECS) ---
  async updateItemSpecs(itemId, newSpecs) {
    // Também atualizamos o full_data.specs para manter consistencia
    const record = await pb.collection("production_orders").getOne(itemId);
    const fullData = record.full_data || {};

    fullData.specs = newSpecs; // Atualiza no JSONzao

    await pb.collection("production_orders").update(itemId, {
      specs: newSpecs, // Atualiza na coluna JSON Specs (Top Level)
      full_data: fullData,
      status: "PEDIDO_MODIFICADO",
      // lastModified: Automático (updated)
    });
  },

  // --- 7. MOVER ITEM DE PEDIDO ---
  async moveItemToOrder(itemId, targetOrderData) {
    const safeOrderNumber =
      targetOrderData.orderNumber || targetOrderData.number || "AVULSO";
    const safeCustomerName =
      targetOrderData.customerName || targetOrderData.name || "Cliente Balcão";

    // Busca record
    const record = await pb.collection("production_orders").getOne(itemId);
    const fullData = record.full_data || {};

    // Atualiza JSON Deep
    if (!fullData.order) fullData.order = {};
    if (!fullData.order.customer) fullData.order.customer = {};

    fullData.order.number = safeOrderNumber;
    fullData.order.customer.name = safeCustomerName;

    // Salva
    await pb.collection("production_orders").update(itemId, {
      full_data: fullData,
      order_number: safeOrderNumber, // Flat
      customer_name: safeCustomerName, // Flat
      status: "PEDIDO_MODIFICADO",
    });
  },

  // --- 8. MARCAR CERTIFICADOS COMO IMPRESSOS ---
  async markCertificatesPrinted(itemsToPrint) {
    const promises = itemsToPrint.map(async (item) => {
      // Pega o full_data atual para não perder nada
      const record = await pb.collection("production_orders").getOne(item.id);
      const fullData = record.full_data || {};

      fullData.certificatePrinted = true;

      return pb.collection("production_orders").update(item.id, {
        certificatePrinted: true,
        full_data: fullData,
      });
    });
    await Promise.all(promises);
  },

  // --- 9. SALVAR DETALHES DOS CORREIOS ---
  async saveCorreiosDetails(items, prepostagemId, codigoObjeto, customAddress = null) {
    const promises = items.map(async (item) => {
      const record = await pb.collection("production_orders").getOne(item.id);
      const fullData = record.full_data || {};

      fullData.correios_prepostagem_id = prepostagemId;
      fullData.correios_codigo_objeto = codigoObjeto;
      fullData.correios_etiqueta_gerada_em = new Date().toISOString();
      if (!fullData.shipping) fullData.shipping = {};
      fullData.shipping.tracking = codigoObjeto;

      if (customAddress) {
        fullData.shipping.modified_address = {
          ...customAddress
        };
      }

      return pb.collection("production_orders").update(item.id, {
        full_data: fullData,
      });
    });
    await Promise.all(promises);
  },

  // --- 10. LIMPAR DETALHES DOS CORREIOS (REPOSTAGEM) ---
  async clearCorreiosDetails(items) {
    const promises = items.map(async (item) => {
      const record = await pb.collection("production_orders").getOne(item.id);
      const fullData = record.full_data || {};

      delete fullData.correios_prepostagem_id;
      delete fullData.correios_codigo_objeto;
      delete fullData.correios_etiqueta_gerada_em;
      if (fullData.shipping) {
        delete fullData.shipping.tracking;
      }

      return pb.collection("production_orders").update(item.id, {
        full_data: fullData,
      });
    });
    await Promise.all(promises);
  },
};
